/**
 * SocialLinkExtractorConfig Component
 *
 * Form-based configuration editor for the social link extractor automation.
 * Edits the nested automation shape: { urls: [...], config: {...scraper settings...} }.
 * URLs live at the top level; scraper settings live under `config`.
 *
 * Presentation follows the shared config DESIGN_GUIDE (badged section headers,
 * rounded-lg inputs, light-first with dark variants) like PageExtractorConfig.
 *
 * @param {Object} props
 * @param {Object} props.config - Current social link extractor automation ({ urls, config })
 * @param {Function} props.onChange - Called with updated automation object
 */

import React from "react";
import { FiPlus, FiX } from "react-icons/fi";
import { FaGlobe, FaSitemap, FaShareAlt, FaFilter } from "react-icons/fa";
import { MdEmail } from "react-icons/md";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const DEPTH_DESCRIPTIONS = {
  1: "Only direct links from the initial pages",
  2: "Links from the initial pages plus their linked pages",
};

const SOCIAL_PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", desc: "Company & profiles" },
  { key: "twitter", label: "Twitter / X", desc: "Profiles & handles" },
  { key: "facebook", label: "Facebook", desc: "Pages & profiles" },
  { key: "instagram", label: "Instagram", desc: "Profiles" },
  { key: "youtube", label: "YouTube", desc: "Channels" },
  { key: "tiktok", label: "TikTok", desc: "Profiles" },
  { key: "github", label: "GitHub", desc: "Repositories & profiles" },
  { key: "pinterest", label: "Pinterest", desc: "Boards & profiles" },
];

// Hide the LinkedIn row until this date. Only the UI row is hidden; the
// underlying config value is left untouched.
const LINKEDIN_OPTION_HIDDEN_UNTIL = new Date("2050-01-01T00:00:00Z");
const VISIBLE_SOCIAL_PLATFORMS = SOCIAL_PLATFORMS.filter(
  (platform) => !(new Date() < LINKEDIN_OPTION_HIDDEN_UNTIL && platform.key === "linkedin")
);

const ACCENTS = {
  blue: { badge: "bg-blue-100 dark:bg-blue-500/15", icon: "text-blue-500 dark:text-blue-400" },
  emerald: { badge: "bg-emerald-100 dark:bg-emerald-500/15", icon: "text-emerald-500 dark:text-emerald-400" },
  orange: { badge: "bg-orange-100 dark:bg-orange-500/15", icon: "text-orange-500 dark:text-orange-400" },
};

const SectionCard = ({ icon: Icon, title, accent = "blue", action, children }) => {
  const a = ACCENTS[accent] || ACCENTS.blue;
  return (
    <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
      <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
        <div className={`p-1.5 rounded-lg ${a.badge} mr-2`}>
          <Icon className={`${a.icon} w-3 h-3`} />
        </div>
        <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">{title}</h3>
        {action && <div className="ml-auto">{action}</div>}
      </div>
      {children}
    </div>
  );
};

const Toggle = ({ checked, onChange, color = "orange", disabled = false }) => {
  const onColor = { blue: "bg-blue-500", orange: "bg-orange-500", emerald: "bg-emerald-500" }[color] || "bg-orange-500";
  return (
    <label className={`relative inline-flex items-center ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className="sr-only" />
      <div className={`w-8 h-4 rounded-full transition-colors ${checked ? onColor : "bg-gray-300 dark:bg-gray-600"}`}>
        <div className={`w-3 h-3 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"} mt-0.5`}></div>
      </div>
    </label>
  );
};

const RequiredBadge = () => (
  <span className="text-2xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 px-1.5 py-0.5 rounded-md">
    Required
  </span>
);

const inputClass =
  "w-full bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white px-3 py-2 rounded-lg text-xs " +
  "border border-gray-300 dark:border-gray-600/50 hover:border-gray-400 dark:hover:border-gray-500/50 " +
  "focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500/20 focus:outline-none " +
  "placeholder-gray-400 dark:placeholder-gray-500 transition-colors";

const numberInputClass =
  "w-20 bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white px-3 py-2 rounded-lg text-sm text-center " +
  "border border-gray-300 dark:border-gray-600/50 hover:border-gray-400 dark:hover:border-gray-500/50 " +
  "focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500/20 focus:outline-none transition-colors";

const SocialLinkExtractorConfig = ({ config = {}, onChange }) => {
  const urls = config.urls || [];
  const c = config.config || {};

  const updateUrls = (newUrls) => onChange({ ...config, urls: newUrls });
  const updateConfigField = (field, value) => onChange({ ...config, config: { ...c, [field]: value } });

  const urlsText = urls.join("\n");
  const handleUrlsChange = (e) => {
    const parsed = e.target.value
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    updateUrls(parsed);
  };

  const handleNumberChange = (field, rawValue, fallback, min, max) => {
    const parsed = Number(rawValue);
    if (Number.isNaN(parsed)) {
      updateConfigField(field, fallback);
      return;
    }
    updateConfigField(field, clamp(parsed, min, max));
  };

  // Inline editor for a plain string array (domainFilters / emailPatterns)
  const renderStringList = (items, field, { placeholder, emptyMessage, mono = false }) => {
    const addItem = () => updateConfigField(field, [...items, ""]);
    const updateItem = (index, value) => {
      const updated = [...items];
      updated[index] = value;
      updateConfigField(field, updated);
    };
    const removeItem = (index) => updateConfigField(field, items.filter((_, i) => i !== index));

    return (
      <div className="space-y-2">
        {items.length === 0 ? (
          <div className="p-3 bg-gray-50 dark:bg-gray-800/30 border border-dashed border-gray-300 dark:border-gray-700/50 rounded-lg text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">{emptyMessage}</p>
          </div>
        ) : (
          items.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={item || ""}
                onChange={(e) => updateItem(index, e.target.value)}
                placeholder={placeholder}
                className={`${inputClass} ${mono ? "font-mono" : ""}`}
              />
              <button
                onClick={() => removeItem(index)}
                className="p-1.5 rounded-lg text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        )}
        <button
          onClick={addItem}
          className="w-full py-1.5 rounded-lg text-xs font-medium border border-dashed border-gray-300 dark:border-gray-600
                   text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-400 dark:hover:border-gray-500 transition-colors
                   flex items-center justify-center gap-1"
        >
          <FiPlus className="w-3 h-3" />
          Add
        </button>
      </div>
    );
  };

  const subPageEnabled = c.enableSubPageScanning === true;
  const emailEnabled = c.enableEmailExtraction !== false;
  const maxDepth = c.maxDepth ?? 1;
  const platforms = c.socialMediaPlatforms || {};
  const isPlatformOn = (key) => platforms[key] !== false;
  const updatePlatform = (key, val) => updateConfigField("socialMediaPlatforms", { ...platforms, [key]: val });

  return (
    <div className="space-y-2.5">
      {/* URLs */}
      <SectionCard
        icon={FaShareAlt}
        title="URLs to Extract"
        accent="blue"
        action={
          <span className="text-2xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/50 px-2 py-0.5 rounded-md">
            {urls.length} URL{urls.length !== 1 ? "s" : ""}
          </span>
        }
      >
        <div className="space-y-2">
          <textarea
            value={urlsText}
            onChange={handleUrlsChange}
            placeholder="https://example.com/page1&#10;https://example.com/page2"
            rows={5}
            className={`${inputClass} font-mono resize-none`}
          />
          <p className="text-2xs text-gray-400 dark:text-gray-500">Enter one URL per line</p>
        </div>
      </SectionCard>

      {/* Extractions */}
      <SectionCard icon={FaShareAlt} title="Extractions" accent="orange">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaShareAlt className="text-orange-500 dark:text-orange-400 w-3.5 h-3.5" />
              <div>
                <label className="text-sm text-gray-700 dark:text-gray-200">Social Link Extraction</label>
                <p className="text-xs text-gray-400 dark:text-gray-500">Find social media profile links</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <RequiredBadge />
              <Toggle checked disabled color="orange" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MdEmail className="text-emerald-500 dark:text-emerald-400 w-3.5 h-3.5" />
              <div>
                <label className="text-sm text-gray-700 dark:text-gray-200">Email Extraction</label>
                <p className="text-xs text-gray-400 dark:text-gray-500">Also find email addresses alongside social links</p>
              </div>
            </div>
            <Toggle
              checked={c.enableEmailExtraction !== false}
              onChange={(e) => updateConfigField("enableEmailExtraction", e.target.checked)}
              color="emerald"
            />
          </div>
        </div>
      </SectionCard>

      {/* Platforms */}
      <SectionCard icon={FaShareAlt} title="Social Platforms" accent="orange">
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">Platforms to detect</label>
            <div className="grid grid-cols-2 gap-2">
              {VISIBLE_SOCIAL_PLATFORMS.map((platform) => (
                <label key={platform.key} className="flex items-center space-x-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 hover:bg-gray-100 dark:hover:bg-gray-800/60 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={isPlatformOn(platform.key)}
                    onChange={(e) => updatePlatform(platform.key, e.target.checked)}
                    className="w-3 h-3 rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-orange-500 focus:ring-orange-500/30"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-200">{platform.label}</div>
                    <div className="text-2xs text-gray-400 dark:text-gray-500">{platform.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Custom URL Patterns</label>
            <textarea
              rows={2}
              value={c.customSocialMediaPatterns || ""}
              onChange={(e) => updateConfigField("customSocialMediaPatterns", e.target.value)}
              placeholder="Enter URL regex patterns&#10;e.g. mastodon\\.social\\/[^\\/]+"
              className={`${inputClass} font-mono resize-none focus:border-orange-500 dark:focus:border-orange-400 focus:ring-orange-500/20`}
            />
            <p className="text-2xs text-gray-400 dark:text-gray-500 mt-1">Regex patterns to match additional social media URLs</p>
          </div>
        </div>
      </SectionCard>

      {/* Email Detection — shown when email extraction is enabled */}
      {emailEnabled && (
        <SectionCard icon={FaFilter} title="Email Detection" accent="emerald">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm text-gray-700 dark:text-gray-200">Include Mailto Links</label>
                <p className="text-xs text-gray-400 dark:text-gray-500">Extract emails from clickable mailto: links</p>
              </div>
              <Toggle checked={c.includeMailtoLinks !== false} onChange={(e) => updateConfigField("includeMailtoLinks", e.target.checked)} color="emerald" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm text-gray-700 dark:text-gray-200">Convert to Lowercase</label>
                <p className="text-xs text-gray-400 dark:text-gray-500">Normalize email case</p>
              </div>
              <Toggle checked={c.toLowerCase !== false} onChange={(e) => updateConfigField("toLowerCase", e.target.checked)} color="emerald" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm text-gray-700 dark:text-gray-200">Basic Validation</label>
                <p className="text-xs text-gray-400 dark:text-gray-500">Filter out invalid email addresses</p>
              </div>
              <Toggle checked={c.basicValidation !== false} onChange={(e) => updateConfigField("basicValidation", e.target.checked)} color="emerald" />
            </div>

            <div>
              <label className="block text-sm text-gray-700 dark:text-gray-200 mb-1">Domain Filters</label>
              <p className="text-2xs text-gray-400 dark:text-gray-500 mb-2">Only include emails from these domains (e.g. @company.com)</p>
              {renderStringList(c.domainFilters || [], "domainFilters", {
                placeholder: "@company.com",
                emptyMessage: "No filters — emails from any domain are kept",
              })}
            </div>

            <div>
              <label className="block text-sm text-gray-700 dark:text-gray-200 mb-1">Custom Patterns</label>
              <p className="text-2xs text-gray-400 dark:text-gray-500 mb-2">Extra regex patterns for custom email formats</p>
              {renderStringList(c.emailPatterns || [], "emailPatterns", {
                placeholder: "[a-z]+@company\\.com",
                emptyMessage: "No custom patterns — standard detection is used",
                mono: true,
              })}
            </div>
          </div>
        </SectionCard>
      )}

      {/* Performance / Timing */}
      <SectionCard icon={FaGlobe} title="Timings" accent="blue">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm text-gray-700 dark:text-gray-200">Request Delay</label>
              <p className="text-xs text-gray-400 dark:text-gray-500">Pause between requests (ms)</p>
            </div>
            <input
              type="number"
              min="0"
              step="100"
              value={c.delayBetweenRequests ?? 0}
              onChange={(e) => handleNumberChange("delayBetweenRequests", e.target.value, 0, 0, Infinity)}
              className={numberInputClass}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm text-gray-700 dark:text-gray-200">Page Load Timeout</label>
              <p className="text-xs text-gray-400 dark:text-gray-500">Max wait for a page to load (ms)</p>
            </div>
            <input
              type="number"
              min="0"
              step="1000"
              value={c.pageLoadTimeout ?? 30000}
              onChange={(e) => handleNumberChange("pageLoadTimeout", e.target.value, 30000, 0, Infinity)}
              className={numberInputClass}
            />
          </div>
        </div>
      </SectionCard>

      {/* Deep Scanning */}
      <SectionCard icon={FaSitemap} title="Deep Scanning" accent="orange">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm text-gray-700 dark:text-gray-200">Enable Sub-page Scanning</label>
              <p className="text-xs text-gray-400 dark:text-gray-500">Follow links to scan additional pages</p>
            </div>
            <Toggle checked={subPageEnabled} onChange={(e) => updateConfigField("enableSubPageScanning", e.target.checked)} color="orange" />
          </div>

          {subPageEnabled && (
            <div className="space-y-3 p-3 rounded-xl bg-orange-50 dark:bg-transparent dark:bg-gradient-to-br dark:from-orange-500/10 dark:to-orange-500/5 border border-orange-200 dark:border-orange-500/30">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-gray-700 dark:text-gray-200">Maximum Depth</label>
                  <span className="text-2xs font-medium text-orange-600 dark:text-orange-300 bg-orange-100 dark:bg-orange-500/20 px-2 py-0.5 rounded-md">Level {maxDepth}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={maxDepth}
                  onChange={(e) => updateConfigField("maxDepth", parseInt(e.target.value))}
                  className="w-full h-1.5 bg-gray-200 dark:bg-gray-700/60 rounded-full appearance-none cursor-pointer slider-orange"
                />
                <div className="flex justify-between text-2xs text-gray-400 dark:text-gray-500 mt-1">
                  <span>1</span>
                  <span>5</span>
                  <span>10</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <strong>Depth {maxDepth}:</strong> {DEPTH_DESCRIPTIONS[maxDepth] || `Up to ${maxDepth} levels of linked pages`}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm text-gray-700 dark:text-gray-200">Max Links Per Page</label>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Limit links followed per page</p>
                </div>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={c.maxLinksPerPage ?? 20}
                  onChange={(e) => handleNumberChange("maxLinksPerPage", e.target.value, 20, 1, 50)}
                  className={`${numberInputClass} w-16 focus:border-orange-500 dark:focus:border-orange-400 focus:ring-orange-500/20`}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm text-gray-700 dark:text-gray-200">Stay on Same Domain</label>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Only follow links within the same domain</p>
                </div>
                <Toggle checked={c.stayOnDomain !== false} onChange={(e) => updateConfigField("stayOnDomain", e.target.checked)} color="orange" />
              </div>

              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200 mb-1">Link Selectors</label>
                <input
                  type="text"
                  value={c.subPageLinkSelectors || ""}
                  onChange={(e) => updateConfigField("subPageLinkSelectors", e.target.value)}
                  placeholder="a[href], .nav-link, .menu-item a"
                  className={`${inputClass} font-mono focus:border-orange-500 dark:focus:border-orange-400 focus:ring-orange-500/20`}
                />
                <p className="text-2xs text-gray-400 dark:text-gray-500 mt-1">CSS selectors for finding links (defaults to a[href])</p>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      <style jsx>{`
        .slider-orange::-webkit-slider-thumb {
          appearance: none;
          height: 14px;
          width: 14px;
          border-radius: 50%;
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          cursor: pointer;
          box-shadow: 0 1px 4px rgba(251, 191, 36, 0.3), 0 0 0 2px rgba(251, 191, 36, 0.1);
          border: none;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .slider-orange::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 2px 6px rgba(251, 191, 36, 0.4), 0 0 0 3px rgba(251, 191, 36, 0.15);
        }
        .slider-orange::-moz-range-thumb {
          height: 14px;
          width: 14px;
          border-radius: 50%;
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          cursor: pointer;
          border: none;
          box-shadow: 0 1px 4px rgba(251, 191, 36, 0.3);
        }
      `}</style>
    </div>
  );
};

export default SocialLinkExtractorConfig;
